/** Sites list + create wizard (name → address → start from a template / AI / blank). Route /sites */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Button, Card, Col, Empty, Form, Input, Modal, Progress, Row, Segmented, Select, Space, Spin, Steps, Tag, Tooltip, Typography,
} from 'antd';
import { EditOutlined, ExportOutlined, LayoutOutlined, PlusOutlined, RobotOutlined, FileOutlined, CheckCircleFilled, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import PageHeader from '../../../shared/components/PageHeader';
import { QUIET, sb } from './api';
import { SITE_SUFFIX, StatusTag, editorPath, fmtDate, listOf, sitePath, siteUrl, slugify, toastErr, useLoad } from './common';
import { AiCreditBadge, reportAiError, startAi } from './ai';
import { PlansModal, fmtSize, showPlans } from './Media';
import SharedSites from './SitesShared';

const { Text, Paragraph } = Typography;
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;
const CAT_COLORS = ['#2357ff', '#0f9d58', '#e8710a', '#a142f4', '#d93025', '#12b5cb', '#f9ab00', '#5f6368'];
const CATEGORY_LABELS = {
    portfolio: 'Portfolio', restaurant: 'Restaurant', shop: 'Online store', agency: 'Agency', event: 'Conferences & events', personal: 'Personal',
    business: 'Business', education: 'Education', beauty: 'Beauty', saas: 'SaaS', blog: 'Blog', nonprofit: 'Nonprofit',
};
const catLabel = (t, c) => t(CATEGORY_LABELS[c] || c);
const hashColor = (s) => CAT_COLORS[[...String(s || '')].reduce((a, c) => a + c.charCodeAt(0), 0) % CAT_COLORS.length];

const TemplateCard = ({ tpl, selected, onClick }) => {
    const { t } = useTranslation();
    const c = hashColor(tpl.category || tpl.name);
    return (
        <Card hoverable size="small" onClick={onClick}
            style={{ borderColor: selected ? '#2357ff' : undefined, boxShadow: selected ? '0 0 0 2px #2357ff33' : undefined, height: '100%' }}
            cover={tpl.thumbnail
                ? <img alt="" src={tpl.thumbnail} style={{ height: 110, objectFit: 'cover' }} />
                : (
                    <div style={{ height: 110, boxSizing: 'border-box', overflow: 'hidden', background: `linear-gradient(135deg, ${c}, ${c}99)`, color: '#fff', padding: 12, position: 'relative' }}>
                        <div style={{ width: '60%', height: 8, background: '#ffffffaa', borderRadius: 4, marginBottom: 8 }} />
                        <div style={{ width: '40%', height: 6, background: '#ffffff77', borderRadius: 4, marginBottom: 14 }} />
                        <div style={{ display: 'flex', gap: 6 }}>{[0, 1, 2].map((i) => <div key={i} style={{ flex: 1, height: 36, background: '#ffffff33', borderRadius: 4 }} />)}</div>
                        {selected && <CheckCircleFilled style={{ position: 'absolute', top: 8, right: 8, fontSize: 18 }} />}
                    </div>
                )}>
            <Text strong ellipsis style={{ display: 'block' }}>{tpl.label || tpl.name}</Text>
            <Paragraph type="secondary" ellipsis={{ rows: 2, tooltip: tpl.description }} style={{ fontSize: 12, marginBottom: 4, minHeight: 36 }}>{tpl.description}</Paragraph>
            <Space size={4} wrap>
                {tpl.category && <Tag style={{ marginInlineEnd: 0 }}>{catLabel(t, tpl.category)}</Tag>}
                {tpl.pages ? <Text type="secondary" style={{ fontSize: 11 }}>{t('{{n}} pages', { n: tpl.pages })}</Text> : null}
            </Space>
        </Card>
    );
};

export const CreateSiteWizard = ({ open, onClose, onCreated, plans = [], free }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [step, setStep] = useState(0);
    const [slugTouched, setSlugTouched] = useState(false);
    const [mode, setMode] = useState('template');
    const [templates, setTemplates] = useState(null);
    const [tpl, setTpl] = useState(null);
    const [cat, setCat] = useState('all');
    const [prompt, setPrompt] = useState('');
    const [busy, setBusy] = useState(false);
    const [aiState, setAiState] = useState(null); // {siteId, status}
    const slug = Form.useWatch('slug', form);

    useEffect(() => {
        if (!open) return;
        setStep(0); setSlugTouched(false); setMode('template'); setTpl(null); setPrompt(''); setAiState(null); setCat('all');
        form.resetFields();
        if (templates === null) sb.get('/templates', null, QUIET).then((d) => setTemplates(listOf(d))).catch(() => setTemplates([]));
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const cats = useMemo(() => ['all', ...new Set((templates || []).map((x) => x.category).filter(Boolean))], [templates]);
    const shown = (templates || []).filter((x) => cat === 'all' || x.category === cat);

    const next = async () => {
        await form.validateFields();
        setStep(1);
    };
    const create = async () => {
        const v = await form.validateFields();
        if (mode === 'ai' && !prompt.trim()) return;
        setBusy(true);
        let site;
        try {
            site = await sb.post('', { name: v.name.trim(), slug: v.slug, template: mode === 'template' && tpl ? tpl : undefined, subscription_id: v.subscription_id || undefined });
        } catch (e) {
            toastErr(e, t('Failed to create site'));
            if (e?.body?.code === 'plan_limit') showPlans();
            if (e?.body?.code === 'slug_taken' || e?.body?.code === 'invalid_slug' || e?.body?.code === 'slug_reserved') setStep(0);
            setBusy(false);
            return;
        }
        onCreated?.(site);
        if (mode === 'ai') {
            try {
                await startAi(site.id, 'site', { prompt: prompt.trim() });
            } catch (e) {
                reportAiError(e);
            }
        }
        setBusy(false);
        onClose();
        navigate(editorPath(site.id));
    };

    const slugErr = slug && (!SLUG_RE.test(slug) || slug.includes('--'));
    return (
        <Modal open={open} onCancel={busy ? undefined : onClose} width={step === 1 ? 920 : 560} title={t('New site')} maskClosable={false} destroyOnHidden
            footer={aiState ? null : (
                <Space>
                    {step === 1 && <Button onClick={() => setStep(0)} disabled={busy}>{t('Back')}</Button>}
                    {step === 0
                        ? <Button type="primary" onClick={next}>{t('Next', { context: 'shop' })}</Button>
                        : <Button type="primary" loading={busy} onClick={create} disabled={(mode === 'template' && !tpl) || (mode === 'ai' && !prompt.trim())}>{t('Create site')}</Button>}
                </Space>
            )}>
            {aiState ? (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                    <RobotOutlined style={{ fontSize: 40, color: '#2357ff' }} />
                    <Paragraph style={{ marginTop: 16 }}>{aiState.status === 'running' ? t('Building your site…') : t('Queued…')}</Paragraph>
                    <Progress percent={aiState.status === 'running' ? 60 : 15} status="active" showInfo={false} style={{ maxWidth: 320, margin: '0 auto' }} />
                </div>
            ) : (
                <>
                    <Steps size="small" current={step} style={{ marginBottom: 20 }} items={[{ title: t('Name') }, { title: t('Start') }]} />
                    <Form form={form} layout="vertical" requiredMark={false} style={{ display: step === 0 ? 'block' : 'none' }}
                        onValuesChange={(ch) => {
                            if ('name' in ch && !slugTouched) form.setFieldsValue({ slug: slugify(ch.name).slice(0, 40).replace(/-+$/, '') });
                            if ('slug' in ch) setSlugTouched(true);
                        }}>
                        <Form.Item name="name" label={t('Site name')} rules={[{ required: true, whitespace: true, message: t('Enter a site name') }, { max: 120 }]}>
                            <Input autoFocus placeholder={t('My business')} onPressEnter={next} />
                        </Form.Item>
                        <Form.Item name="slug" label={t('Address')} normalize={(x) => String(x || '').toLowerCase().replace(/[^a-z0-9-]/g, '')}
                            validateStatus={slugErr ? 'error' : undefined}
                            help={slugErr ? t('3–40 characters: lowercase letters, numbers and hyphens') : undefined}
                            rules={[{ required: true, message: t('Enter an address') }, { validator: (_, x) => (x && (!SLUG_RE.test(x) || x.includes('--')) ? Promise.reject(new Error(t('3–40 characters: lowercase letters, numbers and hyphens'))) : Promise.resolve()) }]}>
                            <Input addonAfter={`.${SITE_SUFFIX}`} maxLength={40} onPressEnter={next} />
                        </Form.Item>
                        {plans.filter((p) => p.status === 'active').length > 0 && (
                            <Form.Item name="subscription_id" label={t('Plan')}>
                                <Select allowClear placeholder={`${t('Free')}${free?.features?.storage_mb ? ` · ${fmtSize(free.features.storage_mb * 1048576)}` : ''}`}
                                    options={plans.filter((p) => p.status === 'active').map((p) => ({
                                        value: p.id,
                                        label: `${p.name}${p.features?.storage_mb ? ` · ${Number(p.features.storage_mb) < 0 ? t('Unlimited') : fmtSize(p.features.storage_mb * 1048576)}` : ''}`,
                                        disabled: Number(p.features?.sites) >= 0 && p.sites_used >= Number(p.features?.sites),
                                    }))} />
                            </Form.Item>
                        )}
                        <Text type="secondary" style={{ fontSize: 12 }}>{t('You can connect your own domain later.')} <a onClick={showPlans}>{t('Plans')}</a></Text>
                    </Form>
                    {step === 1 && (
                        <div>
                            <Segmented value={mode} onChange={setMode} style={{ marginBottom: 16 }}
                                options={[
                                    { value: 'template', icon: <LayoutOutlined />, label: t('Template') },
                                    { value: 'ai', icon: <RobotOutlined />, label: t('Describe your site') },
                                    { value: 'blank', icon: <FileOutlined />, label: t('Blank') },
                                ]} />
                            {mode === 'template' && (
                                templates === null ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                                    : !templates.length ? <Empty description={t('No templates yet')} />
                                        : (
                                            <>
                                                {cats.length > 2 && (
                                                    <Space size={4} wrap style={{ marginBottom: 12 }}>
                                                        {cats.map((c) => <Tag.CheckableTag key={c} checked={cat === c} onChange={() => setCat(c)}>{c === 'all' ? t('All') : catLabel(t, c)}</Tag.CheckableTag>)}
                                                    </Space>
                                                )}
                                                <Row gutter={[12, 12]} style={{ maxHeight: '52vh', overflow: 'auto', padding: 2 }}>
                                                    {shown.map((x) => (
                                                        <Col key={x.name} xs={24} sm={12} md={8}>
                                                            <TemplateCard tpl={x} selected={tpl === x.name} onClick={() => setTpl(x.name)} />
                                                        </Col>
                                                    ))}
                                                </Row>
                                            </>
                                        )
                            )}
                            {mode === 'ai' && (
                                <div>
                                    <div style={{ marginBottom: 8, textAlign: 'right' }}><AiCreditBadge /></div>
                                    <Input.TextArea autoFocus value={prompt} onChange={(e) => setPrompt(e.target.value)} autoSize={{ minRows: 5, maxRows: 10 }} maxLength={4000}
                                        placeholder={t('e.g. "A bakery in Lisbon selling sourdough and pastries online, warm and rustic style, with a menu, about us and contact page"')} />
                                </div>
                            )}
                            {mode === 'blank' && <Alert type="info" showIcon message={t('Starts with an empty home page.')} />}
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
};

const SitesList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [creating, setCreating] = useState(false);
    const { data, loading, reload } = useLoad(() => sb.get(''), []);
    const sites = listOf(data?.sites || data);

    return (
        <div>
            <PageHeader title={t('Sites')} extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>{t('New site')}</Button>} />
            {loading && !data ? <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
                : !sites.length ? (
                    <Card>
                        <Empty image={<LayoutOutlined style={{ fontSize: 56, color: '#2357ff' }} />} imageStyle={{ height: 64 }}
                            description={<Text>{t('Build a website or an online store')}</Text>}>
                            <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => setCreating(true)}>{t('New site')}</Button>
                        </Empty>
                    </Card>
                ) : (
                    <Row gutter={[16, 16]}>
                        {sites.map((s) => {
                            const url = siteUrl(s);
                            return (
                                <Col key={s.id} xs={24} md={12} xl={8}>
                                    <Card hoverable onClick={() => navigate(sitePath(s.id))}
                                        actions={[
                                            <Tooltip key="e" title={t('Edit site')}><EditOutlined onClick={(e) => { e.stopPropagation(); navigate(editorPath(s.id)); }} /></Tooltip>,
                                            <Tooltip key="m" title={t('Manage')}><SettingOutlined onClick={(e) => { e.stopPropagation(); navigate(sitePath(s.id)); }} /></Tooltip>,
                                            <Tooltip key="o" title={t('Open site')}><ExportOutlined onClick={(e) => { e.stopPropagation(); if (s.published_version) window.open(url, '_blank', 'noopener'); }} style={{ opacity: s.published_version ? 1 : 0.3 }} /></Tooltip>,
                                        ]}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                                                <Text strong style={{ fontSize: 16 }} ellipsis>{s.name}</Text>
                                                <StatusTag value={s.status} />
                                            </Space>
                                            <Text type="secondary" ellipsis>{url.replace(/^https?:\/\//, '')}</Text>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {s.published_version ? t('Version {{v}} · {{date}}', { v: s.published_version, date: fmtDate(s.published_at, false) }) : t('Not published yet')}
                                            </Text>
                                        </Space>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                )}
            <div style={{ marginTop: 24 }}><SharedSites /></div>
            <CreateSiteWizard open={creating} onClose={() => setCreating(false)} onCreated={() => reload()} plans={listOf(data?.plans)} free={data?.free} />
            <PlansModal />
        </div>
    );
};

export default SitesList;
